// src/components/AiDashboard/AiMyNutritionStatus.js
// React component for the "My Logged Nutrition Status" assistant.
// - Consumes the Nutrition Calculate JSON snapshot (nutritionTargetsCalculations).
// - Builds a deterministic narrative layout locally from JSON only.
// - No Gemini, no external AI calls.

import React, { useMemo } from "react";
import PropTypes from "prop-types";
import AiMyNutritionStatusResult from "./AiMyNutritionStatusResult";
import buildMyNutritionStatusLayout from "./AiMyNutritionStatus/buildMyNutritionStatusLayout";

/**
 * Small utility to check finite numbers.
 */
function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * React component: AiMyNutritionStatus
 *
 * Props:
 *  - nutritionTargetsCalculations: JSON snapshot for the selected date range.
 *  - userName: name used in the PDF "Prepared for" section.
 *  - db, region, question: legacy props kept for compatibility but not used.
 *
 * It renders a layout that is fully derived locally from JSON, without any
 * network calls or AI models. All text is deterministic for the same input.
 */
function AiMyNutritionStatus({
  // Legacy props kept for compatibility; unused in JSON-driven mode.
  db, // eslint-disable-line no-unused-vars
  region, // eslint-disable-line no-unused-vars
  question, // eslint-disable-line no-unused-vars

  nutritionTargetsCalculations,
  userName = "Valued Member"
}) {
  const hasData = Boolean(
    nutritionTargetsCalculations &&
      nutritionTargetsCalculations.intakeSummary &&
      nutritionTargetsCalculations.intakeSummary.comparisonVsTargets
  );

  const totalDaysLogged = useMemo(() => {
    if (!hasData) return null;
    const value =
      nutritionTargetsCalculations.intakeSummary.totalDaysLogged;
    return isFiniteNumber(value) ? value : null;
  }, [hasData, nutritionTargetsCalculations]);

  /**
   * Build the narrative layout purely from the JSON snapshot.
   * This is deterministic: same JSON → same layout/text.
   */
  const layout = useMemo(() => {
    if (!hasData) {
      return null;
    }
    return buildMyNutritionStatusLayout(nutritionTargetsCalculations);
  }, [hasData, nutritionTargetsCalculations]);

  return (
    <div className="flex flex-col gap-3 p-4 rounded-xl bg-slate-900/60 border border-slate-800">
      <div className="text-sm font-semibold text-slate-200">
        {layout?.title || "My Logged Nutrition Status"}
      </div>

      {!hasData && (
        <div className="text-sm text-slate-400">
          Log some meals and select a date range to see how your
          nutrition compares to your targets.
        </div>
      )}

      {hasData && !layout && (
        <div className="text-sm text-slate-400">
          We could not build a nutrition summary for this period. Try
          selecting a different date range.
        </div>
      )}

      {hasData && layout && (
        <div className="text-sm text-slate-300">
          <div className="flex items-start gap-3">
            {/* Legal PDF Download Preview */}
            <AiMyNutritionStatusResult
              data={layout}
              userName={userName}
            />

            {/* On-screen narrative mirrors the PDF content but avoids duplicating the full disclaimer block */}
            <div className="flex-1 space-y-3">
              {layout.dateRange && (
                <p className="text-slate-400">{layout.dateRange}</p>
              )}

              {layout.macroSummary && <p>{layout.macroSummary}</p>}

              {isFiniteNumber(totalDaysLogged) && (
                <p className="text-xs text-slate-500">
                  Based on {totalDaysLogged} logged{" "}
                  {totalDaysLogged === 1 ? "day" : "days"} in this period.
                </p>
              )}

              {layout.lowNutrients && layout.lowNutrients.length > 0 && (
                <div>
                  <div className="font-semibold text-slate-200">
                    Nutrients that look low
                  </div>
                  <ul className="mt-1 list-disc list-inside space-y-1">
                    {layout.lowNutrients.map((item) => (
                      <li key={`low-${item.key}`}>
                        <span className="font-medium">
                          {item.displayName || item.name}
                        </span>
                        {isFiniteNumber(item.percentOfTarget) && (
                          <span className="text-slate-400">
                            {" "}
                            – around{" "}
                            {Math.round(item.percentOfTarget)}% of your
                            reference intake.
                          </span>
                        )}
                        {item.recommendation && (
                          <span className="text-slate-400">
                            {" "}
                            {item.recommendation}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {layout.highNutrients &&
                layout.highNutrients.length > 0 && (
                  <div>
                    <div className="font-semibold text-slate-200">
                      Nutrients that look high
                    </div>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      {layout.highNutrients.map((item) => (
                        <li key={`high-${item.key}`}>
                          <span className="font-medium">
                            {item.displayName || item.name}
                          </span>
                          {isFiniteNumber(item.percentOfTarget) && (
                            <span className="text-slate-400">
                              {" "}
                              – around{" "}
                              {Math.round(item.percentOfTarget)}% of
                              your reference intake.
                            </span>
                          )}
                          {item.recommendation && (
                            <span className="text-slate-400">
                              {" "}
                              {item.recommendation}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              {layout.onTrackNutrients &&
                layout.onTrackNutrients.length > 0 && (
                  <div>
                    <div className="font-semibold text-slate-200">
                      Nutrients that look on track
                    </div>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      {layout.onTrackNutrients.map((item) => (
                        <li key={`ontrack-${item.key}`}>
                          <span className="font-medium">
                            {item.displayName || item.name}
                          </span>
                          {isFiniteNumber(item.percentOfTarget) && (
                            <span className="text-slate-400">
                              {" "}
                              – around{" "}
                              {Math.round(item.percentOfTarget)}% of
                              your reference intake.
                            </span>
                          )}
                          {item.recommendation && (
                            <span className="text-slate-400">
                              {" "}
                              {item.recommendation}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              {/* Inline "how to use this" guidance (shorter than full PDF disclaimer) */}
              <p className="text-xs text-slate-400">
                Before eating on a day within this period, open the{" "}
                <span className="font-semibold">
                  Suggested Consumption
                </span>
                ,{" "}
                <span className="font-semibold">
                  Macros
                </span>{" "}
                and{" "}
                <span className="font-semibold">
                  Vitamins &amp; Minerals
                </span>{" "}
                views for the same dates to see which nutrients are
                already high or low. When you add a meal, open the{" "}
                <span className="font-semibold">
                  Item Nutrition
                </span>{" "}
                view to see how that specific item contributes to your
                totals. This summary is based only on your logged meals
                and is not medical advice.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

AiMyNutritionStatus.propTypes = {
  db: PropTypes.object,
  nutritionTargetsCalculations: PropTypes.object,
  region: PropTypes.string,
  question: PropTypes.string,
  userName: PropTypes.string
};

export default AiMyNutritionStatus;
